import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DashboardUIMode } from '../types/dashboard-ui-enums';
import { DashboardUIState, DashboardUIStore } from '../types/dashboard-filter-types';

const initialUIState: DashboardUIState = {
  isEditMode: false,
  selectedMode: DashboardUIMode.VIEW,
  isPageDialogOpen: false,
  isViewDialogOpen: false,
  isAddWidgetDialogOpen: false,
  isDashboardDialogOpen: false,
  saveAsNewDialogOpen: false,
  createViewDialogOpen: false,
  editViewDialogOpen: false,
  editedPage: null,
  editedView: null,
  editingDashboard: null,
  newViewName: '',
  createViewName: '',
  editViewName: '',
  editingViewId: null,
  tempFilterConfig: null,
  isCreatingDashboard: false,
};

export const useDashboardUIStore = create<DashboardUIStore>()(
  devtools(
    (set, get) => ({
      ...initialUIState,

      // Edit mode actions
      enterEditMode: () => set({ isEditMode: true }, false, 'enterEditMode'),
      exitEditMode: () => set({ isEditMode: false }, false, 'exitEditMode'),
      toggleEditMode: () => set({ isEditMode: !get().isEditMode }, false, 'toggleEditMode'),

      // Mode selection
      setSelectedMode: mode => set({ selectedMode: mode }, false, 'setSelectedMode'),

      // Page dialog
      openPageDialog: (initialData = null) =>
        set(
          {
            isPageDialogOpen: true,
            editedPage: initialData ? { ...initialData } : null,
          },
          false,
          'openPageDialog'
        ),
      closePageDialog: () =>
        set(
          {
            isPageDialogOpen: false,
            editedPage: null,
          },
          false,
          'closePageDialog'
        ),

      // View dialog
      openViewDialog: (initialData = null) =>
        set(
          {
            isViewDialogOpen: true,
            editedView: initialData ? { ...initialData } : null,
          },
          false,
          'openViewDialog'
        ),
      closeViewDialog: () =>
        set(
          {
            isViewDialogOpen: false,
            editedView: null,
          },
          false,
          'closeViewDialog'
        ),

      // Add widget dialog
      openAddWidgetDialog: () => set({ isAddWidgetDialogOpen: true }, false, 'openAddWidgetDialog'),
      closeAddWidgetDialog: () =>
        set({ isAddWidgetDialogOpen: false }, false, 'closeAddWidgetDialog'),

      // Dashboard dialog
      openDashboardDialog: (dashboard = null) =>
        set(
          {
            isDashboardDialogOpen: true,
            editingDashboard: dashboard,
          },
          false,
          'openDashboardDialog'
        ),
      closeDashboardDialog: () =>
        set(
          {
            isDashboardDialogOpen: false,
            editingDashboard: null,
          },
          false,
          'closeDashboardDialog'
        ),

      // Save as new dialog
      openSaveAsNewDialog: (defaultName = 'New Dashboard View') =>
        set(
          {
            saveAsNewDialogOpen: true,
            newViewName: defaultName,
          },
          false,
          'openSaveAsNewDialog'
        ),
      closeSaveAsNewDialog: () =>
        set(
          {
            saveAsNewDialogOpen: false,
            newViewName: '',
          },
          false,
          'closeSaveAsNewDialog'
        ),

      // Create view dialog
      openCreateViewDialog: () =>
        set(
          {
            createViewDialogOpen: true,
            createViewName: '',
          },
          false,
          'openCreateViewDialog'
        ),
      closeCreateViewDialog: () =>
        set(
          {
            createViewDialogOpen: false,
            createViewName: '',
          },
          false,
          'closeCreateViewDialog'
        ),

      // Edit view dialog
      openEditViewDialog: (viewId, viewName = '') =>
        set(
          {
            editViewDialogOpen: true,
            editingViewId: viewId,
            editViewName: viewName,
          },
          false,
          'openEditViewDialog'
        ),
      closeEditViewDialog: () =>
        set(
          {
            editViewDialogOpen: false,
            editingViewId: null,
            editViewName: '',
          },
          false,
          'closeEditViewDialog'
        ),

      // Name setters
      setNewViewName: name => set({ newViewName: name }, false, 'setNewViewName'),
      setCreateViewName: name => set({ createViewName: name }, false, 'setCreateViewName'),
      setEditViewName: name => set({ editViewName: name }, false, 'setEditViewName'),

      // Loading states
      setIsCreatingDashboard: isCreating =>
        set({ isCreatingDashboard: isCreating }, false, 'setIsCreatingDashboard'),

      // Temp filter config
      setTempFilterConfig: config =>
        set({ tempFilterConfig: config }, false, 'setTempFilterConfig'),

      // Reset all UI state
      resetUIState: () => set(initialUIState, false, 'resetUIState'),
    }),
    { name: 'DashboardUIStore', enabled: process.env.NODE_ENV === 'development' }
  )
);

// Re-export for convenience
export default useDashboardUIStore;

